import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { IApiResponse } from 'api/api-models';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { isNotUndefined } from 'lib/helpers/array.utils';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { ITable, ITableField } from 'lib/ui/table/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

export type ListAdminUsersFilter = 'all' | 'clients' | 'trainers' | 'premium' | 'referrers' | 'expiredUsers';
export interface IListAdminComponentConfig {
	filter: ListAdminUsersFilter;
}

type ListEndpoint =
	| 'listAdminUsers'
	| 'listAdminClients'
	| 'listAdminTrainers'
	| 'listAdminPremiumUsers'
	| 'listExpiredUsers'
	| 'listAdminReferrerUsers';

@Component({
	selector: 'mod-list-admin-users',
	templateUrl: './list-admin-users.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ListAdminUsersComponent extends TypeCoreComponent<IListAdminComponentConfig> implements OnInit {
	public readonly table = signal<ITable<UserDb.IUserItem> | undefined>(undefined);
	private readonly endpointsMap: Record<ListAdminUsersFilter, ListEndpoint> = {
		all: 'listAdminUsers',
		clients: 'listAdminClients',
		expiredUsers: 'listExpiredUsers',
		premium: 'listAdminPremiumUsers',
		referrers: 'listAdminReferrerUsers',
		trainers: 'listAdminTrainers'
	};

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.initUsers(
				sharedData?.data ?? {
					filter: 'all'
				}
			);
		};
	}

	private initUsers(config: IListAdminComponentConfig): void {
		const joinedField: ITableField<UserDb.IUserItem> = {
			format: 'date',
			title: `Joined Date`,
			value: (item) => this.dependencies.timeService.toDate(item.system.created),
			sortBy: (item) => new Date(item.system.created),
			isDefaultSort: false,
			showOnMobiles: true
		};

		const clientOfField: (response: IApiResponse<readonly UserDb.IUserItem[]>) => ITableField<UserDb.IUserItem> = (
			response
		) => {
			return {
				format: 'text',
				title: `Client of`,
				value: (item) => {
					if (!item.trainerId) {
						return undefined;
					}

					const trainer = response.linkedItems
						?.filter(UserDb.isUserItem)
						.find((m) => m.id === item.trainerId);

					if (trainer) {
						return `Client of ${trainer.email}`;
					}
					return `Invalid trainer`;
				},
				sortBy: (item) => item.trainerId,
				isDefaultSort: false,
				showOnMobiles: true
			};
		};

		const emailField: ITableField<UserDb.IUserItem> = {
			format: 'text',
			title: `E-mail`,
			value: (item) => item.email,
			sortBy: (item) => item.email,
			showOnMobiles: true
		};

		const browserField: ITableField<UserDb.IUserItem> = {
			format: 'text',
			title: `Browser`,
			value: (item) =>
				[item.browser?.browserName, item.browser?.browserVersion, item.browser?.platform]
					.filter(isNotUndefined)
					.join(' / '),
			showOnMobiles: true
		};

		const referrerField: ITableField<UserDb.IUserItem> = {
			format: 'text',
			title: `Referrer`,
			value: (item) => item.referrer,
			showOnMobiles: true
		};

		const endpoint: ListEndpoint = this.endpointsMap[config.filter];

		super.subscribeToObservable(
			this.dependencies.httpService.listCustom<UserDb.IUserItem>('user', endpoint).pipe(
				map((response) => {
					this.table.set({
						itemType: 'User',
						noItemsText: {
							noItems: `There are no users`,
							noFilteredItems: `No users match your filter`
						},
						search: {
							type: 'local'
						},
						items: response.data,
						avatar: (item) => {
							return {
								name: item.email,
								image: item.avatar
							};
						},
						id: (item) => item.id,
						title: {
							title: 'Users',
							value: (item) => nameHelper.getUserName(item.email, item.firstName, item.lastName)
						},
						mainRouterLink: (item) => {
							return super
								.actionRoute((routes) => routes.adminUserRoutes.user.children.view, item.id)
								.getUrl();
						},

						subtitle2: (item) => {
							return `${item.timezone ? `${item.timezone}` : ''}`;
						},
						subtitle: (item) => {
							if (!item.trainerId) {
								return undefined;
							}

							const trainer = response.linkedItems
								?.filter(UserDb.isUserItem)
								.find((m) => m.id === item.trainerId);

							if (trainer) {
								return `Client of ${trainer.email}`;
							}
							return `Invalid trainer`;
						},
						fields: [
							browserField,
							...(this.isDesktopExtraView() ? [referrerField] : []),
							{
								format: 'relativeTime',
								title: `Last active`,
								value: (item) =>
									item.lastActiveDate
										? this.dependencies.timeService.fromNow(item.lastActiveDate)
										: 'n/a',
								sortBy: (item) =>
									item.lastActiveDate ? new Date(item.lastActiveDate) : new Date(1970),
								showOnMobiles: true,
								isDefaultSort: true
							},
							...(this.isMobileView()
								? [emailField, clientOfField(response), referrerField, joinedField].filter(
										isNotUndefined
								  )
								: [])
						]
					});
				})
			)
		);
	}
}
